import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { quizRegistry } from "@quizzes/_registry";
import type { Question, Quiz } from "@quizzes/_schema";
import {
  DateRangePicker,
  isoDaysAgo,
  isoToday,
  toRangeIso,
} from "../components/DateRangePicker";
import { FunnelChart } from "../components/FunnelChart";
import { type FunnelResponse, api, ApiError } from "../api";

function questionLabel(q: Question): string {
  return q.type === "info" ? q.heading : q.prompt;
}

function questionLabelMap(quiz: Quiz): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of quiz.questions) out[q.id] = questionLabel(q);
  return out;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  const seconds = Math.round(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function Analytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const quizzes = useMemo(() => Object.values(quizRegistry), []);
  const [slug, setSlug] = useState<string>(
    searchParams.get("slug") ?? quizzes[0]?.slug ?? "",
  );
  const [from, setFrom] = useState<string>(isoDaysAgo(7));
  const [to, setTo] = useState<string>(isoToday());
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const quiz = quizRegistry[slug];

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("slug", slug);
    setSearchParams(next, { replace: true });
  }, [slug, searchParams, setSearchParams]);

  useEffect(() => {
    if (!quiz) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const range = toRangeIso(from, to);
    api
      .funnel({
        slug: quiz.slug,
        from: range.from,
        to: range.to,
        questionIds: quiz.questions.map((q) => q.id),
      })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message || `HTTP ${err.status}`);
        else setError("Failed to load funnel");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quiz, from, to]);

  if (!quiz) {
    return <div className="text-mars-white/60">No quizzes registered.</div>;
  }

  const labels = questionLabelMap(quiz);
  const completionLabel =
    data?.completion_rate === null || data?.completion_rate === undefined
      ? "—"
      : `${data.completion_rate}%`;

  return (
    <div>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="eyebrow">Funnel</span>
          <h1 className="heading mt-1 text-3xl">Analytics</h1>
          <p className="mt-2 text-sm text-mars-white/60">
            Per-question viewed/answered, drop-off, completion rate.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="eyebrow">Quiz</span>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="mono rounded-md border border-mars-line bg-mars-night px-3 py-1.5 text-sm text-mars-white outline-none transition-colors focus:border-mars-orange"
            >
              {quizzes.map((q) => (
                <option key={q.slug} value={q.slug}>
                  {q.meta.title} ({q.slug})
                </option>
              ))}
            </select>
          </label>
          <DateRangePicker
            from={from}
            to={to}
            onChange={(next) => {
              setFrom(next.from);
              setTo(next.to);
            }}
          />
        </div>
      </header>

      <section className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Starts" value={data?.started ?? "—"} />
        <Stat label="Completions" value={data?.completed ?? "—"} />
        <Stat label="Completion rate" value={completionLabel} accent />
        <Stat label="Avg duration" value={formatDuration(data?.avg_duration_ms ?? null)} />
      </section>

      {loading ? (
        <div className="rounded-2xl border border-mars-line bg-mars-charcoal/40 p-6 text-sm text-mars-white/60">
          Loading funnel...
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-900/60 bg-red-950/40 p-6 text-sm text-red-200">
          {error}
        </div>
      ) : data ? (
        <FunnelChart
          started={data.started}
          completed={data.completed}
          steps={data.steps}
          questionLabels={labels}
        />
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-mars-line bg-mars-charcoal/40 px-5 py-4">
      <div className="eyebrow">{label}</div>
      <div
        className={
          accent
            ? "mono mt-2 text-3xl font-bold tabular-nums text-mars-orange"
            : "mono mt-2 text-3xl font-bold tabular-nums text-mars-white"
        }
      >
        {value}
      </div>
    </div>
  );
}
